// SPA entry. Wires the editor, output panel, mode switch, and worker bridge.

// Vite worker query: this static import resolves to the worker's bundled URL (dev: the
// transpiled source; prod: the hashed asset). Strapping the URL onto a `Worker` is
// Vite's recommended ESM-friendly shape — it survives the prod hashing pass without us
// having to hand-roll `new URL("./worker.js", import.meta.url)` glue.
import workerUrl from "./bridge/worker.ts?worker&url";
import { createBridgeClient } from "./bridge/client.ts";
import { mountEditor, mountReplInput, DEFAULT_FILE, SQL_EXAMPLE } from "./editor/editor.ts";
import { OutputPanel } from "./output.ts";
import { createFileMode } from "./modes/file-mode.ts";
import { createReplMode } from "./modes/repl-mode.ts";
import { createProgressBar } from "./progress.ts";
import { createSqlPanel } from "./sql-panel.ts";
import type { StreamEvent } from "./bridge/client.ts";
import { decodeShare, encodeShare } from "./util/share.ts";

type Mode = "file" | "sql" | "repl";

type Theme = "light" | "dark";
const THEME_STORAGE_KEY = "rell-playground:theme";
const MODE_STORAGE_KEY = "rell-playground:mode";

function readSavedMode(): Mode | null {
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    return v === "file" || v === "sql" || v === "repl" ? v : null;
  } catch {
    return null;
  }
}

function saveMode(mode: Mode): void {
  try { localStorage.setItem(MODE_STORAGE_KEY, mode); } catch { /* private mode */ }
}

// The editor keeps a separate, independently-persisted buffer per editable mode:
// editing the Run program and then visiting SQL dry-run shows SQL's own code, and
// coming back to Run restores the edited Run program. REPL has no buffer — it shares
// (and writes through to) whichever buffer was active when it was entered.
// `file` reuses the original single-buffer key so existing saved programs survive.
type BufferMode = "file" | "sql";
const BUFFER_KEYS: Record<BufferMode, string> = {
  file: "rell-playground:buffer",
  sql: "rell-playground:buffer:sql",
};

function bufferExample(m: BufferMode): string {
  return m === "sql" ? SQL_EXAMPLE : DEFAULT_FILE;
}

function readBuffer(m: BufferMode): string {
  try { return localStorage.getItem(BUFFER_KEYS[m]) ?? bufferExample(m); }
  catch { return bufferExample(m); }
}

function saveBuffer(m: BufferMode, value: string): void {
  try { localStorage.setItem(BUFFER_KEYS[m], value); } catch { /* private mode */ }
}

function readTheme(): Theme {
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "dark" ? "dark" : "light";
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* private mode */ }
  // Notify any subscribers (Monaco) that the theme changed.
  document.dispatchEvent(new CustomEvent("themechange", { detail: { theme } }));
}

async function main(): Promise<void> {

  const outputEl = document.getElementById("output");
  const sqlEl = document.getElementById("sql");
  const editorEl = document.getElementById("editor");
  const runBtn = document.getElementById("run-btn") as HTMLButtonElement | null;
  const stopBtn = document.getElementById("stop-btn") as HTMLButtonElement | null;
  const shareBtn = document.getElementById("share-btn") as HTMLButtonElement | null;
  const themeToggle = document.getElementById("theme-toggle") as HTMLButtonElement | null;
  const versionTag = document.getElementById("version-tag");
  const replForm = document.getElementById("repl-form") as HTMLElement | null;
  const replInputEl = document.getElementById("repl-input") as HTMLElement | null;
  const tabOutput = document.getElementById("tab-output") as HTMLButtonElement | null;
  const tabSql = document.getElementById("tab-sql") as HTMLButtonElement | null;
  const modeButtons = Array.from(document.querySelectorAll<HTMLButtonElement>(".mode-switch button"));

  if (!outputEl || !sqlEl || !editorEl || !runBtn || !stopBtn || !shareBtn || !themeToggle || !versionTag ||
      !replForm || !replInputEl || !tabOutput || !tabSql) {
    throw new Error("missing required DOM nodes");
  }

  // Reflect current theme in the toggle glyph, and wire the click.
  const refreshToggleGlyph = (): void => {
    themeToggle.textContent = readTheme() === "dark" ? "☀" : "☾";
  };
  refreshToggleGlyph();
  themeToggle.addEventListener("click", () => {
    applyTheme(readTheme() === "dark" ? "light" : "dark");
    refreshToggleGlyph();
  });

  // Optional shared buffer from URL hash. Overrides the localStorage cache.
  // A `sql:` prefix targets the SQL dry-run mode instead of Run; bare hashes
  // stay Run-mode so pre-prefix share links keep working.
  let initial: string | undefined;
  let initialMode: BufferMode = "file";
  if (location.hash.length > 1) {
    const shared = await decodeShare(location.hash.slice(1));
    if (shared !== null) {
      initial = shared.text;
      initialMode = shared.mode;
    }
  }

  const output = new OutputPanel(outputEl);
  const sql = createSqlPanel(sqlEl);
  const progress = createProgressBar();
  // A shared buffer from the URL hash seeds (and persists into) its target
  // mode's buffer; otherwise the editor opens on the Run mode's saved buffer.
  // For a sql-mode share the editor still mounts on the Run buffer — the
  // setMode("sql") call after bridge init swaps the shared program in.
  if (initial !== undefined) saveBuffer(initialMode, initial);
  const editor = await mountEditor(
    editorEl,
    initialMode === "file" && initial !== undefined ? initial : readBuffer("file"),
  );
  const replInput = mountReplInput(replInputEl);
  // Which buffer the editor is currently bound to for persistence. Editing in any
  // mode writes through to this buffer; switching modes re-points it.
  let bufferMode: BufferMode = "file";
  editor.onChange((v) => saveBuffer(bufferMode, v));

  // Output / SQL tab switching.
  const setTab = (which: "output" | "sql"): void => {
    const isOutput = which === "output";
    outputEl.hidden = !isOutput;
    sqlEl.hidden = isOutput;
    tabOutput.classList.toggle("active", isOutput);
    tabSql.classList.toggle("active", !isOutput);
    tabOutput.setAttribute("aria-selected", isOutput ? "true" : "false");
    tabSql.setAttribute("aria-selected", isOutput ? "false" : "true");
  };
  tabOutput.addEventListener("click", () => setTab("output"));
  tabSql.addEventListener("click", () => setTab("sql"));

  const bridge = createBridgeClient(new URL(workerUrl, import.meta.url));

  // Tracked so route() can reveal the Output tab if a SQL dry-run hits a
  // real diagnostic (compile error / non-trivial runtime error) — see below.
  let currentMode: Mode = "file";
  let revealOutputOnError = (): void => {};

  // Funnel stream events: progress → bar, sql → panel, everything else → output.
  const route = (e: StreamEvent): void => {
    if (e.kind === "progress") {
      if (e.done) progress.hide();
      else progress.set(e.loaded, e.total, e.label);
      return;
    }
    if (e.kind === "sql") {
      sql.append(e.text);
      return;
    }
    // In SQL dry-run mode the Output tab is hidden (the pane would otherwise
    // just show empty results). But a compile error or genuine runtime error
    // IS worth showing — surface the Output tab when one arrives.
    if (currentMode === "sql" && (e.kind === "compiler" || e.kind === "runtimeError")) {
      revealOutputOnError();
    }
    output.appendEvent(e);
  };

  progress.show("Loading Rell runtime…");
  output.appendLine("Loading Rell runtime...", "system");
  // Safari/WebKit silently stalls compiling the multi-MB TeaVM bridge module
  // (no error, no timeout, no console output). Race init against a wall-clock
  // budget so users on broken browsers get told instead of staring forever.
  const INIT_TIMEOUT_MS = 20_000;
  const initTimeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("init-timeout")), INIT_TIMEOUT_MS),
  );
  try {
    await Promise.race([bridge.init(route), initTimeout]);
  } catch (e) {
    progress.hide();
    if (e instanceof Error && e.message === "init-timeout") {
      output.appendLine(
        "Could not load the Rell runtime — your browser hung while compiling it.",
        "error",
      );
      output.appendLine(
        "Safari is known to stall on the bridge module. Please try Chrome or Firefox.",
        "error",
      );
      return;
    }
    throw e;
  }
  progress.hide();
  const version = await bridge.version();
  // Full string is something like:
  //   "rell: 0.15.4; postchain: 3.49.10; branch: master; commit: …; dirty: true"
  // Surface just the Rell version in the topbar — postchain is irrelevant for
  // the in-browser playground (no node, no consensus). The full descriptor stays
  // on `title=` for discoverability.
  const m = /rell:\s*([^;]+)/i.exec(version);
  const rell = m?.[1]?.trim();
  versionTag.textContent = rell ? `rell ${rell}` : version;
  versionTag.title = version;
  // In Output, surface only the Rell version — postchain, branch, commit, and the
  // dirty flag are noise here. The full descriptor stays on `title=`.
  const shown = m ? `rell: ${rell}` : version;
  output.appendLine(`Ready — ${shown}`, "system");

  // Phased indeterminate progress bar during Run/SQL/REPL execution. The worker call
  // (`bridge.runFile` etc.) is one synchronous JVM-in-JS block so we can't get genuine
  // sub-phase signals from inside; instead we flip the label on a timer to communicate
  // that something is happening. Thresholds are tuned to typical TeaVM-bridge timings:
  // a trivial `print` round-trips in ~200 ms, a fact(10) in ~500 ms, and a SQL dry-run
  // with several entities takes 2–4 s on first hit.
  let phaseTimer: ReturnType<typeof setInterval> | null = null;
  let showTimer: ReturnType<typeof setTimeout> | null = null;
  const phaseLabel = (ms: number): string => {
    if (ms < 600) return "Compiling Rell…";
    if (ms < 2500) return "Running…";
    return "Working…";
  };
  const stopPhases = (): void => {
    if (showTimer !== null) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    if (phaseTimer !== null) {
      clearInterval(phaseTimer);
      phaseTimer = null;
    }
  };
  // Defer ALL busy chrome — the progress bar, the disabled Run button, and the
  // enabled Stop button — behind one timer. Trivial runs finish in ~200 ms, so
  // flashing any of it on then immediately off just makes the topbar and buttons
  // twitch. Only reveal once a run has clearly outlasted the window; fast runs
  // never paint it at all and the buttons hold their resting state.
  const SHOW_DELAY_MS = 250;
  const startPhases = (): void => {
    stopPhases();
    const t0 = performance.now();
    showTimer = setTimeout(() => {
      showTimer = null;
      runBtn.disabled = true;
      stopBtn.disabled = false;
      progress.show(phaseLabel(performance.now() - t0));
      phaseTimer = setInterval(() => {
        progress.set(0, 0, phaseLabel(performance.now() - t0));
      }, 150);
    }, SHOW_DELAY_MS);
  };

  // Source of truth for "a run is in flight" — independent of the button DOM,
  // which now lags behind by SHOW_DELAY_MS.
  let isBusy = false;
  const busy = (b: boolean): void => {
    isBusy = b;
    if (b) startPhases();
    else {
      stopPhases();
      runBtn.disabled = false;
      stopBtn.disabled = true;
      progress.hide();
    }
  };

  const clearPanels = (): void => {
    output.clear();
    sql.clear();
  };
  const beginRender = (): void => {
    output.beginRender();
    sql.beginRender();
  };
  const endRender = (): void => {
    output.endRender();
    sql.endRender();
  };
  // Plain "Run" → bridge.runFile (REPL-on-one-command). SQL dry-run →
  // bridge.runModule (module compile + SQL capture). Same UI machinery.
  const fileMode = createFileMode(
    (code, onEvent) => bridge.runFile(code, onEvent), editor, output, busy, route, beginRender, endRender);
  const sqlMode = createFileMode(
    (code, onEvent) => bridge.runModule(code, onEvent), editor, output, busy, route, beginRender, endRender);
  const replMode = createReplMode(bridge, editor, output, busy, route, clearPanels);

  // Which output tabs a mode exposes:
  //   file / repl → Output only (no SQL is generated worth showing)
  //   sql         → SQL only, Output hidden until a real diagnostic appears
  const applyTabsForMode = (m: Mode): void => {
    const sqlOnly = m === "sql";
    tabSql.hidden = !sqlOnly;
    tabOutput.hidden = sqlOnly;
    setTab(sqlOnly ? "sql" : "output");
  };
  revealOutputOnError = (): void => {
    tabOutput.hidden = false;
    setTab("output");
  };

  let mode: Mode = "file";
  applyTabsForMode(mode);

  // Cancel any in-flight bridge call. The worker is single-threaded JVM-in-JS, so
  // a pending compile/run can't be observed mid-flight — the only way to abort
  // is to terminate the worker (which `bridge.reset` does) and respawn. Any pending
  // promises reject with "worker terminated"; their try/finally blocks fire setBusy(false).
  const cancelInFlight = (reason: string): void => {
    if (!isBusy) return; // not busy → nothing to cancel
    bridge.reset();
    output.appendLine(reason, "system");
    busy(false);
  };

  const setMode = async (next: Mode) => {
    if (next === mode) return;
    // Switching modes while a previous run is still compiling/executing leaves
    // the user staring at a "Working…" spinner that's still tied to the old
    // mode's panels. Halt the worker so the new mode starts clean.
    cancelInFlight(`Cancelled: switched to ${next} mode.`);
    if (mode === "repl") await replMode.leave();
    // Persist the outgoing editor content to whichever buffer it's bound to before
    // swapping in the incoming mode's buffer below.
    saveBuffer(bufferMode, editor.getValue());
    mode = next;
    currentMode = next;
    saveMode(mode);
    // Mode switches reset both panes: a REPL transcript / dry-run SQL is
    // meaningless after toggling. Keeps the user from staring at stale output.
    clearPanels();
    for (const b of modeButtons) {
      const active = b.dataset["mode"] === mode;
      b.setAttribute("aria-selected", active ? "true" : "false");
    }
    replForm.hidden = mode !== "repl";
    // Drive CSS off `body[data-mode=...]`. REPL hides the editor entirely and
    // gives the output panel the full width — the input bar at the bottom is
    // the only way to send commands in that mode.
    document.body.dataset["mode"] = mode;
    // No editor in REPL mode → Run button has nothing meaningful to do.
    runBtn.hidden = mode === "repl";
    applyTabsForMode(mode);
    // Load the incoming mode's own buffer. REPL keeps the current editor content
    // (and its existing buffer binding) — it shares whatever's already shown.
    if (mode !== "repl") {
      bufferMode = mode;
      editor.setValue(readBuffer(mode));
    }
    if (mode === "repl") {
      await replMode.enter();
      replInput.focus();
    } else {
      editor.focus();
    }
  };
  for (const b of modeButtons) {
    b.addEventListener("click", () => {
      const m = b.dataset["mode"];
      if (m === "file" || m === "sql" || m === "repl") void setMode(m);
    });
  }

  const runCurrent = (): void => {
    if (isBusy) return; // re-entrancy guard — the Run button no longer disables synchronously
    if (mode === "file") void fileMode.run();
    else if (mode === "sql") void sqlMode.run();
    else void replMode.runEditor();
  };
  runBtn.addEventListener("click", runCurrent);

  stopBtn.addEventListener("click", () => {
    bridge.reset();
    output.appendLine("Stopped. Worker restarted.", "system");
    if (mode === "repl") void replMode.enter();
    busy(false);
  });

  replInput.onSubmit((cmd) => {
    void replMode.submitLine(cmd);
  });

  shareBtn.addEventListener("click", async () => {
    // Sharing from SQL dry-run keeps the recipient in SQL dry-run.
    const encoded = await encodeShare(editor.getValue(), mode === "sql" ? "sql" : "file");
    const url = `${location.origin}${location.pathname}#${encoded}`;
    try {
      await navigator.clipboard.writeText(url);
      output.appendLine("Share URL copied to clipboard.", "system");
    } catch {
      output.appendLine(`Share URL: ${url}`, "system");
    }
    history.replaceState(null, "", `#${encoded}`);
  });

  document.addEventListener("keydown", (ev) => {
    if ((ev.ctrlKey || ev.metaKey) && ev.key === "Enter") {
      ev.preventDefault();
      runBtn.click();
    }
  });

  editor.focus();

  // Restore last-used mode from localStorage. We defer this until after bridge.init()
  // succeeded so REPL's `enter()` (which calls replCreate on the worker) has something
  // to talk to. setMode is a no-op when the saved mode equals the current default.
  // Skip restoration when a shared program was loaded from the hash — we open the
  // mode the share link targets instead. A sql-mode share needs an explicit switch
  // here (the shared program was seeded into the SQL buffer above).
  const saved = readSavedMode();
  if (initial !== undefined) {
    if (initialMode !== mode) void setMode(initialMode);
  } else if (saved !== null && saved !== mode) {
    void setMode(saved);
  }
}

void main().catch((e) => {
  // eslint-disable-next-line no-console
  console.error(e);
  const el = document.getElementById("output");
  if (el) {
    const line = document.createElement("div");
    line.className = "line error";
    line.textContent = `fatal: ${e instanceof Error ? e.message : String(e)}`;
    el.appendChild(line);
  }
});
